import React from "react";
import { CheckCircle, CalendarCheck, CurrencyInr, ShieldCheck } from "@phosphor-icons/react";

/* HTML replicas for screens we do not ship a real screenshot of.
   Each is a separate export, so swapping in a real screenshot later = change one import. */

export const DischargeReplica = ({ lang = "en" }) => {
  const t = lang === "hi"
    ? { title: "डिस्चार्ज — दो-की", doctor: "डॉक्टर: सारांश साइन", cashier: "कैशियर: बक़ाया क्लियर", done: "डिस्चार्ज पूर्ण", dues: "बक़ाया", cleared: "क्लियर", summary: "डिस्चार्ज सारांश", patient: "खुशी पाल · GF6 · दिन 7" }
    : { title: "Discharge — two keys", doctor: "Doctor: summary signed", cashier: "Cashier: dues cleared", done: "Discharge complete", dues: "Dues", cleared: "Cleared", summary: "Discharge summary", patient: "Khushi Pal · GF6 · Day 7" };
  return (
    <div className="w-full rounded-xl bg-[#F5F7FB] p-4">
      <p className="font-display text-xs font-bold text-navy">{t.title}</p>
      <p className="mt-0.5 text-[10px] text-slate-500">{t.patient}</p>
      <div className="mt-3 space-y-2">
        {[[t.doctor, true], [t.cashier, true], [t.done, false]].map(([label, ok], i) => (
          <div key={label} className={`flex items-center justify-between rounded-xl border px-3 py-2.5 ${ok ? "border-teal/30 bg-teal/[0.07]" : "border-navy/[0.07] bg-white"}`}>
            <span className="flex items-center gap-2 text-[11px] font-bold text-navy">
              <span className={`flex h-5 w-5 items-center justify-center rounded-full text-[9px] font-black ${ok ? "bg-teal text-white" : "bg-navy/10 text-navy/60"}`}>{i + 1}</span>
              {label}
            </span>
            {ok ? <CheckCircle size={15} weight="fill" className="text-teal-ink" /> : <span className="text-[9px] font-bold uppercase text-slate-400">…</span>}
          </div>
        ))}
      </div>
      <div className="mt-3 flex items-center justify-between rounded-xl border border-navy/[0.07] bg-white px-3 py-2.5">
        <span className="text-[11px] font-semibold text-navy">{t.dues}</span>
        <span className="font-display text-sm font-black text-teal-ink">₹0 · {t.cleared}</span>
      </div>
      <div className="mt-2 rounded-xl border border-navy/[0.07] bg-white px-3 py-2.5 text-[11px] font-semibold text-navy">{t.summary} · PDF</div>
    </div>
  );
};

export const HospitalSiteReplica = ({ lang = "en" }) => {
  const t = lang === "hi"
    ? { name: "डेमो हॉस्पिटल गोरखपुर", tag: "ऑर्थो · न्यूरो · जनरल मेडिसिन", book: "अपॉइंटमेंट बुक करें", docs: "हमारे डॉक्टर", today: "आज उपलब्ध", call: "कॉल करें" }
    : { name: "Demo Hospital Gorakhpur", tag: "Ortho · Neuro · General medicine", book: "Book appointment", docs: "Our doctors", today: "Available today", call: "Call" };
  const docs = lang === "hi"
    ? [["डॉ. अनिल त्रिपाठी", "ऑर्थोपेडिक्स"], ["डॉ. सरिता सिंह", "जनरल मेडिसिन"], ["डॉ. नेहा पांडे", "न्यूरोलॉजी"]]
    : [["Dr Anil Tripathi", "Orthopaedics"], ["Dr Sarita Singh", "General medicine"], ["Dr Neha Pandey", "Neurology"]];
  return (
    <div className="w-full overflow-hidden rounded-xl bg-white">
      <div className="flex items-center justify-between border-b border-navy/[0.07] px-4 py-2.5">
        <span className="font-display text-[11px] font-black text-navy">{t.name}</span>
        <span className="rounded-full bg-electric-ink px-2.5 py-1 text-[9px] font-bold text-white">{t.book}</span>
      </div>
      <div className="bg-navy px-4 py-6">
        <p className="font-display text-base font-black leading-tight text-white">{t.name}</p>
        <p className="mt-1 text-[10px] text-slate-300">{t.tag}</p>
        <div className="mt-3 flex gap-2">
          <span className="rounded-full bg-electric-ink px-3 py-1.5 text-[9px] font-bold text-white">{t.book}</span>
          <span className="rounded-full border border-white/30 px-3 py-1.5 text-[9px] font-bold text-white">{t.call}</span>
        </div>
      </div>
      <div className="p-4">
        <p className="text-[10px] font-bold uppercase tracking-wide text-slate-500">{t.docs}</p>
        <div className="mt-2 grid grid-cols-3 gap-2">
          {docs.map(([n, sp]) => (
            <div key={n} className="rounded-lg border border-navy/[0.07] p-2.5">
              <span className="block h-8 w-8 rounded-full bg-navy/10" />
              <p className="mt-2 text-[10px] font-bold leading-tight text-navy">{n}</p>
              <p className="text-[9px] text-slate-500">{sp}</p>
              <span className="mt-1.5 inline-block rounded-full bg-teal/10 px-2 py-0.5 text-[8px] font-bold text-teal-ink">{t.today}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export const BookingPhoneReplica = ({ lang = "en", className = "" }) => {
  const t = lang === "hi"
    ? { title: "स्लॉट चुनें", date: "सोम, 20 अक्टूबर", confirm: "बुकिंग पक्की करें", upi: "UPI से भुगतान · शून्य कमीशन" }
    : { title: "Pick a slot", date: "Mon, 20 October", confirm: "Confirm booking", upi: "Pay by UPI · zero commission" };
  const slots = ["09:30", "10:00", "10:30", "11:00", "11:30", "12:00"];
  return (
    <div className={`w-[176px] rounded-[1.6rem] border-[6px] border-navy-deep bg-navy-deep shadow-[0_18px_40px_rgba(5,11,20,0.45)] ${className}`}>
      <div className="overflow-hidden rounded-[1.1rem] bg-white">
        <div className="flex items-center gap-2 bg-navy px-3 py-2">
          <CalendarCheck size={12} weight="fill" className="text-teal" />
          <span className="font-display text-[10px] font-bold text-white">{t.title}</span>
        </div>
        <div className="p-2.5">
          <p className="text-[9px] font-bold text-navy">{t.date}</p>
          <div className="mt-2 grid grid-cols-2 gap-1.5">
            {slots.map((sl, i) => (
              <span key={sl} className={`rounded-lg border px-2 py-1.5 text-center text-[9px] font-bold ${i === 2 ? "border-electric-ink bg-electric-ink text-white" : "border-navy/[0.07] text-navy/70"}`}>{sl}</span>
            ))}
          </div>
          <div className="mt-2.5 rounded-lg bg-navy px-2 py-1.5 text-center text-[9px] font-bold text-white">{t.confirm}</div>
          <p className="mt-2 flex items-center gap-1 text-[8px] font-semibold text-teal-ink"><CurrencyInr size={10} weight="bold" /> {t.upi}</p>
        </div>
      </div>
    </div>
  );
};

export const ApprovalPhoneReplica = ({ lang = "en", className = "" }) => {
  const t = lang === "hi"
    ? { title: "अप्रूवल", ask: "छूट अनुरोध", who: "कैशियर · रिंकी देवी", amt: "₹1,200 छूट — बिल ₹8,400", approve: "मंज़ूर", reject: "अस्वीकार", audit: "हर फ़ैसला ऑडिट में दर्ज" }
    : { title: "Approvals", ask: "Discount request", who: "Cashier · Rinki Devi", amt: "₹1,200 off — bill ₹8,400", approve: "Approve", reject: "Decline", audit: "Every decision is recorded" };
  return (
    <div className={`w-[176px] rounded-[1.6rem] border-[6px] border-navy-deep bg-navy-deep shadow-[0_18px_40px_rgba(5,11,20,0.45)] ${className}`}>
      <div className="overflow-hidden rounded-[1.1rem] bg-white">
        <div className="flex items-center gap-2 bg-navy px-3 py-2">
          <ShieldCheck size={12} weight="fill" className="text-teal" />
          <span className="font-display text-[10px] font-bold text-white">{t.title}</span>
        </div>
        <div className="p-2.5">
          <div className="rounded-lg border border-navy/[0.07] p-2.5">
            <p className="text-[10px] font-bold text-navy">{t.ask}</p>
            <p className="mt-0.5 text-[9px] text-slate-500">{t.who}</p>
            <p className="mt-1.5 font-display text-[11px] font-black text-navy">{t.amt}</p>
            <div className="mt-2 flex gap-1.5">
              <span className="flex-1 rounded-md bg-teal px-2 py-1 text-center text-[9px] font-bold text-white">{t.approve}</span>
              <span className="flex-1 rounded-md border border-navy/15 px-2 py-1 text-center text-[9px] font-bold text-navy/70">{t.reject}</span>
            </div>
          </div>
          <p className="mt-2 text-[8px] font-semibold text-slate-500">{t.audit}</p>
        </div>
      </div>
    </div>
  );
};
